import { OnDestroy, DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Firestore,
  Timestamp
} from '@angular/fire/firestore';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthStore } from '@core/services/auth/auth.store';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { PropertyStore } from '../../../../shared/services/property.store';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import {
  Maintenance} from '../../../../shared/definitions/maintenance';
import {
  MaintenanceFrequency,
  MaintenanceStatus
} from '../../../../shared/definitions/maintenance';
import { MaintenanceLog } from '../../../../shared/definitions/maintenance-log';
import { IMaintenanceStore } from './maintenance-store.interface';

/**
 * Firestore implementation of the workspace-scoped maintenance store.
 *
 * Owns the real-time `collectionData` subscription on the workspace-scoped
 * maintenance collection plus all CRUD + log/completion-history subcollection
 * I/O. Replaces the legacy `MaintenanceService` retired as part of Phase C.
 *
 * Gating: re-subscribes when the authenticated user, `claimsReady`, or the
 * current workspace changes.
 *
 * Recurring-task logic (auto-schedule next occurrence on completion) is
 * preserved verbatim from the legacy service.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceFirestoreStore implements IMaintenanceStore, OnDestroy {
  private readonly collectionName = 'maintenance';

  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fs = inject(FirestoreApi);
  private readonly pathService = inject(FirestorePathService);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly propertyStore = inject(PropertyStore);

  private readonly _maintenance = signal<readonly Maintenance[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly maintenance = this._maintenance.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._maintenance().length);
  readonly activeCount = computed(() =>
    this._maintenance().filter(m =>
      m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Canceled
    ).length
  );
  readonly completedCount = computed(() =>
    this._maintenance().filter(m => m.status === MaintenanceStatus.Completed).length
  );

  readonly counts = computed(() => {
    const out = { scheduled: 0, inProgress: 0, completed: 0, overdue: 0, canceled: 0, pending: 0, total: 0 };
    for (const m of this._maintenance()) {
      out.total++;
      switch (m.status) {
        case MaintenanceStatus.Scheduled: out.scheduled++; break;
        case MaintenanceStatus.InProgress: out.inProgress++; break;
        case MaintenanceStatus.Completed: out.completed++; break;
        case MaintenanceStatus.Overdue: out.overdue++; break;
        case MaintenanceStatus.Canceled: out.canceled++; break;
        case MaintenanceStatus.Pending: out.pending++; break;
      }
    }
    return out;
  });

  private sub?: Subscription;
  private currentWorkspaceId: string | null = null;
  /** (user, workspace) scope the live listener is currently open on — guards
   *  `fetchAll` against re-subscribing (and re-paying the whole collection)
   *  when a `refresh()` call-site fires on an unchanged scope. Mirrors
   *  base-crud.service.ts:499. */
  private lastFetchedScopeKey: string | null = null;

  constructor() {
    effect(() => {
      const user = this.authStore.user();
      const claimsReady = this.authStore.claimsReady();
      const workspace = this.workspaceStore.currentWorkspace();
      if (!user?.userUID || !workspace?.workspaceId) {
        this.clear();
        this.currentWorkspaceId = null;
        return;
      }
      if (!claimsReady) return;
      if (workspace.workspaceId !== this.currentWorkspaceId) {
        this.currentWorkspaceId = workspace.workspaceId;
        this.fetchAll();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  async refresh(force = false): Promise<void> {
    this.fetchAll(force);
  }

  getMaintenanceById(maintenanceUID: string): Maintenance | null {
    return this._maintenance().find(m => m.maintenanceUID === maintenanceUID) ?? null;
  }

  getMaintenanceByStatus(status: MaintenanceStatus): readonly Maintenance[] {
    return this._maintenance().filter(m => m.status === status);
  }

  getMaintenanceForProperty(propertyUID: string): readonly Maintenance[] {
    return this._maintenance().filter(m => m.propertyUID === propertyUID);
  }

  getMaintenanceForBuilding(buildingUID: string): readonly Maintenance[] {
    return this._maintenance().filter(m => m.buildingUID === buildingUID);
  }

  getActiveMaintenance(): readonly Maintenance[] {
    return this._maintenance().filter(m =>
      m.status !== MaintenanceStatus.Completed && m.status !== MaintenanceStatus.Canceled
    );
  }

  async addMaintenance(maintenance: Maintenance): Promise<string> {
    const firebaseUser = this.authStore.getCurrentFirebaseUser();
    const currentUser = this.authStore.user();
    if (!firebaseUser || !currentUser?.userUID) {
      throw new Error('User must be authenticated to add maintenance tasks');
    }

    if (!maintenance.title?.trim()) throw new Error('Title is required for maintenance tasks');
    // Description is optional in the create wizard (only the title gates step 1),
    // so never reject a task for an empty description — normalise it to '' to keep
    // the Firestore payload well-formed.
    if (!maintenance.description?.trim()) maintenance.description = '';
    if (!maintenance.propertyUID?.trim() && !maintenance.buildingUID?.trim()) {
      throw new Error('Either a property or a building must be linked to a maintenance task');
    }

    try {
      maintenance.ownerUID = firebaseUser.uid;
      maintenance.createdAt = new Date();
      maintenance.updatedAt = new Date();

      if (maintenance.propertyUID && (!maintenance.propertyName || maintenance.propertyName.trim() === '')) {
        const name = this.propertyStore.getPropertyNameByUID(maintenance.propertyUID);
        if (name !== 'Unknown Property') maintenance.propertyName = name;
      }

      const collectionRef = this.getCollection();
      const docRef = this.fs.doc(collectionRef);
      maintenance.maintenanceUID = docRef.id;

      const payload = this.sanitize({
        ...maintenance,
        maintenanceUID: maintenance.maintenanceUID,
        ownerUID: maintenance.ownerUID,
        title: maintenance.title,
        description: maintenance.description
      });

      await this.fs.setDoc(docRef, payload);
      // Idempotent prepend: the live `collectionData` listener may have
      // already pushed this doc via the Firestore local-cache update by
      // the time `setDoc` resolves. Skipping when the UID is present
      // prevents a transient duplicate row in property-scoped views that
      // snapshot the signal synchronously after `refresh()`.
      const current = this._maintenance();
      if (!current.some(m => m.maintenanceUID === maintenance.maintenanceUID)) {
        this._maintenance.set([maintenance, ...current]);
      }
      return docRef.id;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      throw error;
    }
  }

  async updateMaintenance(maintenance: Maintenance): Promise<void> {
    if (!maintenance.maintenanceUID) throw new Error('Maintenance UID is required for updates');

    const firebaseUser = this.authStore.getCurrentFirebaseUser();
    const currentUser = this.authStore.user();
    if (!firebaseUser || !currentUser?.userUID) {
      throw new Error('User must be authenticated to update maintenance tasks');
    }

    try {
      maintenance.updatedAt = new Date();
      if (maintenance.status === MaintenanceStatus.Completed && !maintenance.completedDate) {
        maintenance.completedDate = new Date();
      }
      if (maintenance.propertyUID && (!maintenance.propertyName || maintenance.propertyName.trim() === '')) {
        const name = this.propertyStore.getPropertyNameByUID(maintenance.propertyUID);
        if (name !== 'Unknown Property') maintenance.propertyName = name;
      }

      const payload = this.sanitize({
        ...maintenance,
        maintenanceUID: maintenance.maintenanceUID,
        ownerUID: firebaseUser.uid,
        title: maintenance.title,
        description: maintenance.description
      });

      await this.fs.setDoc(this.getDocument(maintenance.maintenanceUID), payload, { merge: true });
      // Optimistic update.
      this._maintenance.set(
        this._maintenance().map(m =>
          m.maintenanceUID === maintenance.maintenanceUID ? { ...m, ...maintenance } : m
        )
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      throw error;
    }
  }

  async updateMaintenanceStatus(
    maintenanceUID: string,
    status: MaintenanceStatus,
     
    _note?: string
  ): Promise<void> {
    const maintenance = this._maintenance().find(m => m.maintenanceUID === maintenanceUID);
    if (!maintenance) throw new Error('Maintenance task not found');

    // Mutate a shallow clone to avoid side effects on the signal-backed object.
    const task: Maintenance = { ...maintenance } as Maintenance;
    task.status = status;

    if (status === MaintenanceStatus.Completed) {
      const completionDate = new Date();
      task.completedDate = completionDate;

      if (task.frequency && task.frequency !== MaintenanceFrequency.OneTime) {
        const nextDate = this.scheduleNextMaintenance(task);
        if (nextDate) {
          await this.addCompletionHistory(task, completionDate);
          task.status = MaintenanceStatus.Scheduled;
          task.scheduledDate = nextDate;
          task.completedDate = undefined;
          task.notes = '';
          await this.addGeneralLog(
            maintenanceUID,
            'Next Occurrence Scheduled',
            `Task has been scheduled for next occurrence on ${nextDate.toLocaleDateString('fr-BE')}`
          );
        }
      }
    }

    await this.updateMaintenance(task);
  }

  async deleteMaintenance(maintenanceUID: string): Promise<void> {
    const firebaseUser = this.authStore.getCurrentFirebaseUser();
    const currentUser = this.authStore.user();
    if (!firebaseUser || !currentUser?.userUID) {
      throw new Error('User must be authenticated to delete maintenance tasks');
    }

    try {
      await this.fs.deleteDoc(this.getDocument(maintenanceUID));
      // Optimistic update.
      this._maintenance.set(this._maintenance().filter(m => m.maintenanceUID !== maintenanceUID));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this._lastError.set(error);
      throw error;
    }
  }

  async updateMaintenanceStatusWithLog(
    maintenanceUID: string,
    status: MaintenanceStatus,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<void> {
    await this.updateMaintenanceStatus(maintenanceUID, status);
    const log = new MaintenanceLog(maintenanceUID, `Status Changed to ${status}`, notes);
    if (documentUIDs && documentUIDs.length > 0) log.documentUIDs = documentUIDs;
    await this.addMaintenanceLog(log);
  }

  async addGeneralLog(
    maintenanceUID: string,
    action: string,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<string> {
    const log = new MaintenanceLog(maintenanceUID, action, notes);
    if (documentUIDs && documentUIDs.length > 0) log.documentUIDs = documentUIDs;
    return this.addMaintenanceLog(log);
  }

  async getCompletionHistory(maintenanceUID: string): Promise<Record<string, unknown>[]> {
    const currentUser = this.authStore.user();
    if (!currentUser?.userUID) return [];

    const historyCollection = this.fs.collection(this.getDocument(maintenanceUID), 'completion_history');
    const historyQuery = this.fs.query(historyCollection, this.fs.orderBy('completionDate', 'desc'));

    const rows = await firstValueFrom(this.fs.collectionData(historyQuery, { idField: 'historyUID' }));
    return rows.map(record => {
      const r = record as Record<string, unknown>;
      if (r['completionDate']) r['completionDate'] = this.toDate(r['completionDate']);
      if (r['scheduledDate']) r['scheduledDate'] = this.toDate(r['scheduledDate']);
      if (r['createdAt']) r['createdAt'] = this.toDate(r['createdAt']);
      return r;
    });
  }

  async getMaintenanceLogs(maintenanceUID: string): Promise<MaintenanceLog[]> {
    const currentUser = this.authStore.user();
    if (!currentUser?.userUID) return [];

    const logCollection = this.fs.collection(this.getDocument(maintenanceUID), 'logs');
    const logQuery = this.fs.query(logCollection, this.fs.orderBy('date', 'desc'));

    const rows = await firstValueFrom(this.fs.collectionData(logQuery, { idField: 'logUID' }));
    return rows.map(raw => {
      const r = raw as Record<string, unknown>;
      const log = new MaintenanceLog(maintenanceUID, r['action'] as string, r['notes'] as string | undefined);
      log.logUID = r['logUID'] as string;
      log.userUID = r['userUID'] as string;
      log.userName = r['userName'] as string;
      log.documentUIDs = (r['documentUIDs'] as string[]) || [];
      log.date = this.toDate(r['date']) || new Date();
      return log;
    });
  }

  // --- internals ---

  private fetchAll(force = false): void {
    if (!this.currentWorkspaceId) return;
    const scopeKey = `${this.authStore.user()?.userUID}:${this.currentWorkspaceId}`;
    // Already streaming this exact (user, workspace) scope — the live listener
    // already reflects every change, so re-subscribing only re-pays the whole
    // collection. Skip unless a manual refresh forces it.
    if (!force && this.sub && !this.sub.closed && this.lastFetchedScopeKey === scopeKey) {
      return;
    }
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = scopeKey;
    this._loading.set(true);
    try {
      const ref = this.getCollection();
      this.sub = this.fs.collectionData(ref, { idField: 'maintenanceUID' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
        next: rows => {
          const tasks = rows.map(raw => this.buildMaintenance(raw));
          this._maintenance.set(tasks);
          this._loading.set(false);
        },
        error: err => {
          this._lastError.set(err instanceof Error ? err : new Error(String(err)));
          this._loading.set(false);
        }
      });
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      this._loading.set(false);
    }
  }

  private buildMaintenance(raw: unknown): Maintenance {
    const m = raw as Maintenance;
    if (m.scheduledDate) m.scheduledDate = this.toDate(m.scheduledDate) as Date;
    if (m.completedDate) m.completedDate = this.toDate(m.completedDate);
    if (m.createdAt) m.createdAt = (this.toDate(m.createdAt) as Date) || m.createdAt;
    if (m.updatedAt) m.updatedAt = (this.toDate(m.updatedAt) as Date) || m.updatedAt;
    if (m.propertyUID && (!m.propertyName || m.propertyName.trim() === '')) {
      const name = this.propertyStore.getPropertyNameByUID(m.propertyUID);
      if (name !== 'Unknown Property') m.propertyName = name;
    }
    return m;
  }

  private clear(): void {
    this.sub?.unsubscribe();
    this.lastFetchedScopeKey = null;
    this._maintenance.set([]);
  }

  private getCollection() {
    return this.fs.collection(this.firestore, this.pathService.getWorkspaceCollection(this.collectionName));
  }

  private getDocument(maintenanceUID: string) {
    return this.fs.doc(this.firestore, this.pathService.getWorkspaceDocument(this.collectionName, maintenanceUID));
  }

  private scheduleNextMaintenance(maintenance: Maintenance): Date | null {
    if (!maintenance.frequency || maintenance.frequency === MaintenanceFrequency.OneTime) return null;
    const baseDate = maintenance.completedDate || maintenance.scheduledDate;
    if (!baseDate) return null;
    const nextDate = new Date(baseDate);
    switch (maintenance.frequency) {
      case MaintenanceFrequency.Weekly: nextDate.setDate(nextDate.getDate() + 7); break;
      case MaintenanceFrequency.Monthly: nextDate.setMonth(nextDate.getMonth() + 1); break;
      case MaintenanceFrequency.Quarterly: nextDate.setMonth(nextDate.getMonth() + 3); break;
      case MaintenanceFrequency.BiAnnually: nextDate.setMonth(nextDate.getMonth() + 6); break;
      case MaintenanceFrequency.Annually: nextDate.setFullYear(nextDate.getFullYear() + 1); break;
      default: return null;
    }
    return nextDate;
  }

  private async addCompletionHistory(maintenance: Maintenance, completionDate: Date): Promise<string> {
    if (!maintenance.maintenanceUID) throw new Error('Maintenance task ID is required');
    const currentUser = this.authStore.user();
    if (!currentUser?.userUID) throw new Error('User must be authenticated to add completion history');

    const historyCollection = this.fs.collection(this.getDocument(maintenance.maintenanceUID), 'completion_history');
    const record: Record<string, unknown> = {
      completionDate,
      status: MaintenanceStatus.Completed,
      attachments: maintenance.attachments || [],
      createdAt: new Date()
    };
    if (maintenance.scheduledDate !== undefined) record['scheduledDate'] = maintenance.scheduledDate;
    if (maintenance.notes !== undefined) record['notes'] = maintenance.notes;
    if (maintenance.estimatedCost !== undefined) record['estimatedCost'] = maintenance.estimatedCost;
    if (maintenance.assignedTo !== undefined) record['assignedTo'] = maintenance.assignedTo;

    const ref = await this.fs.addDoc(historyCollection, this.sanitize(record));
    await this.addGeneralLog(
      maintenance.maintenanceUID,
      'Maintenance Completed',
      `Task completed on ${completionDate.toLocaleDateString('fr-BE')}. Next instance has been scheduled.`
    );
    return ref.id;
  }

  private async addMaintenanceLog(log: MaintenanceLog): Promise<string> {
    const currentUser = this.authStore.user();
    if (!currentUser?.userUID) throw new Error('User must be authenticated to add maintenance log');

    log.userUID = currentUser.userUID;
    log.userName = currentUser.firstName
      ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim()
      : currentUser.email;

    const logData: Record<string, unknown> = {
      date: log.date,
      action: log.action,
      userUID: log.userUID,
      userName: log.userName,
      documentUIDs: log.documentUIDs || []
    };
    if (log.notes !== undefined) logData['notes'] = log.notes;

    const logCollection = this.fs.collection(this.getDocument(log.maintenanceUID), 'logs');
    const ref = await this.fs.addDoc(logCollection, logData);
    return ref.id;
  }

  private sanitize(data: Record<string, unknown>): Record<string, unknown> {
    const clean: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined) continue;
      if (value instanceof Date) clean[key] = Timestamp.fromDate(value);
      else if (Array.isArray(value)) clean[key] = value.filter(item => item !== undefined);
      else if (value !== null && typeof value === 'object') clean[key] = this.sanitize(value as Record<string, unknown>);
      else clean[key] = value;
    }
    return clean;
  }

  private toDate(value: unknown): Date | undefined {
    if (!value) return undefined;
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate();
    }
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
  }
}
